/**
 * DelegateAccessManager - Manage delegate access to meals, accommodation, lounge, etc.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  User, QrCode, UtensilsCrossed, Home, Sofa, Star,
  Loader2, Download, CheckCircle, XCircle, Search,
  CreditCard, Package, Shield
} from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';

const ACCESS_TYPES = [
  { key: 'meal_access', label: 'Meals', icon: UtensilsCrossed, description: 'Access to event meals' },
  { key: 'accommodation_access', label: 'Accommodation', icon: Home, description: 'Access to housing/dormitory' },
  { key: 'session_access', label: 'Sessions', icon: Shield, description: 'Access to event sessions' },
  { key: 'lounge_access', label: 'Lounge', icon: Sofa, description: 'VIP lounge access' },
  { key: 'vip_access', label: 'VIP', icon: Star, description: 'VIP area access' },
];

const DelegateAccessManager = ({ eventId, delegateId = null, delegateName = null, compact = false }) => {
  const queryClient = useQueryClient();
  const [selectedDelegate, setSelectedDelegate] = useState(delegateId);
  const [searchQuery, setSearchQuery] = useState('');
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [accessNotes, setAccessNotes] = useState('');

  // Fetch delegates if no specific delegate provided
  const { data: delegatesData, isLoading: loadingDelegates } = useQuery({
    queryKey: ['delegates-access', eventId, searchQuery],
    queryFn: () => api.get(`/admin/events/${eventId}/delegates`, {
      params: { search: searchQuery, limit: 100 }
    }).then(r => r.data),
    enabled: !delegateId,
  });

  const delegates = delegatesData?.delegates || delegatesData || [];

  // Fetch access for selected delegate
  const { data: accessData, isLoading: loadingAccess } = useQuery({
    queryKey: ['delegate-access', eventId, selectedDelegate],
    queryFn: () => api.get(`/admin/events/${eventId}/delegates/${selectedDelegate}/access`).then(r => r.data),
    enabled: !!selectedDelegate,
  });

  // Update access mutation
  const updateAccessMutation = useMutation({
    mutationFn: (data) => api.put(`/admin/events/${eventId}/delegates/${selectedDelegate}/access`, data),
    onSuccess: () => {
      toast.success('Access updated successfully');
      queryClient.invalidateQueries(['delegate-access', eventId, selectedDelegate]);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update access'))
  });

  // Get QR code mutation
  const getQRCodeMutation = useMutation({
    mutationFn: (delegateId) => api.get(`/admin/events/${eventId}/delegates/${delegateId}/qr-code`).then(r => r.data),
    onSuccess: (data) => {
      setQrCodeData(data);
      setShowQRDialog(true);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to generate QR code'))
  });

  const handleAccessToggle = (accessKey, value) => {
    updateAccessMutation.mutate({
      [accessKey]: value,
      access_notes: accessNotes || undefined
    });
  };

  const downloadQRCode = () => {
    if (!qrCodeData?.qr_code_url) return;
    
    const link = document.createElement('a');
    link.href = qrCodeData.qr_code_url;
    link.download = `qr-${qrCodeData.delegate_name || 'delegate'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getPaymentStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-700',
      partial: 'bg-amber-100 text-amber-700',
      pending: 'bg-red-100 text-red-700',
      waived: 'bg-blue-100 text-blue-700'
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  // Compact mode for embedding in delegate details
  if (compact && delegateId) {
    if (loadingAccess) {
      return <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading access...</div>;
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium">Access Permissions</h4>
          <Button
            size="sm"
            variant="outline"
            onClick={() => getQRCodeMutation.mutate(delegateId)}
            disabled={getQRCodeMutation.isPending}
          >
            {getQRCodeMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <QrCode className="h-4 w-4 mr-1" />
            )}
            QR Code
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {ACCESS_TYPES.map((type) => {
            const isEnabled = accessData?.access?.[type.key];
            return (
              <div
                key={type.key}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  isEnabled ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <type.icon className={`h-4 w-4 ${isEnabled ? 'text-green-600' : 'text-slate-400'}`} />
                  <span className="text-sm font-medium">{type.label}</span>
                </div>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={(v) => handleAccessToggle(type.key, v)}
                  disabled={updateAccessMutation.isPending}
                />
              </div>
            );
          })}
        </div>

        {/* QR Code Dialog */}
        <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
          <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Delegate QR Code</DialogTitle>
            </DialogHeader>
            {qrCodeData && (
              <div className="text-center space-y-4">
                <p className="font-medium">{qrCodeData.delegate_name}</p>
                <img
                  src={qrCodeData.qr_code_url}
                  alt="QR Code"
                  className="mx-auto w-64 h-64 border rounded-lg"
                />
                <p className="text-xs text-slate-500 font-mono">{qrCodeData.qr_data}</p>
                <Button onClick={downloadQRCode}>
                  <Download className="h-4 w-4 mr-2" />
                  Download QR Code
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Full page mode
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Delegate Access Management</h2>
        <p className="text-slate-500">Manage access permissions for meals, accommodation, and more</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Delegate List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Delegates</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search delegates..."
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              {loadingDelegates ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                </div>
              ) : (
                <div className="space-y-2">
                  {delegates.map((delegate) => (
                    <div
                      key={delegate.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedDelegate === delegate.id
                          ? 'bg-indigo-50 border-indigo-300'
                          : 'hover:bg-slate-50'
                      }`}
                      onClick={() => setSelectedDelegate(delegate.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <div>
                            <p className="font-medium text-sm">{delegate.name}</p>
                            <p className="text-xs text-slate-500">{delegate.email}</p>
                          </div>
                        </div>
                        {getPaymentStatusBadge(delegate.payment_status || 'pending')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Access Details */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">
              {accessData ? `Access for ${accessData.name}` : 'Select a Delegate'}
            </CardTitle>
            {accessData && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">{accessData.email}</span>
                {getPaymentStatusBadge(accessData.payment_status)}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {!selectedDelegate ? (
              <div className="text-center py-12 text-slate-500">
                <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a delegate to manage their access</p>
              </div>
            ) : loadingAccess ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
              </div>
            ) : accessData ? (
              <div className="space-y-6">
                {/* Access Toggles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ACCESS_TYPES.map((type) => {
                    const isEnabled = accessData.access?.[type.key];
                    return (
                      <div
                        key={type.key}
                        className={`p-4 rounded-lg border-2 transition-colors ${
                          isEnabled ? 'bg-green-50 border-green-300' : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full ${isEnabled ? 'bg-green-200' : 'bg-slate-200'}`}>
                              <type.icon className={`h-5 w-5 ${isEnabled ? 'text-green-700' : 'text-slate-500'}`} />
                            </div>
                            <div>
                              <p className="font-medium">{type.label}</p>
                              <p className="text-xs text-slate-500">{type.description}</p>
                            </div>
                          </div>
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={(v) => handleAccessToggle(type.key, v)}
                            disabled={updateAccessMutation.isPending}
                          />
                        </div>
                        <div className="flex items-center gap-1 text-sm">
                          {isEnabled ? (
                            <>
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span className="text-green-700">Enabled</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-4 w-4 text-slate-400" />
                              <span className="text-slate-500">Disabled</span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Tier Benefits */}
                {accessData.tier_benefits?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Tier Benefits
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {accessData.tier_benefits.map((benefit, i) => (
                        <Badge key={i} variant="secondary">{benefit}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Purchased Add-ons */}
                {accessData.purchased_addons?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Purchased Add-Ons
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {accessData.purchased_addons.map((addon, i) => (
                        <Badge key={i} className="bg-indigo-100 text-indigo-700">{addon}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <Label>Access Notes</Label>
                  <Textarea
                    value={accessNotes}
                    onChange={(e) => setAccessNotes(e.target.value)}
                    placeholder="Add notes about this delegate's access..."
                    rows={2}
                  />
                </div>

                {/* QR Code Button */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => getQRCodeMutation.mutate(selectedDelegate)}
                    disabled={getQRCodeMutation.isPending}
                  >
                    {getQRCodeMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <QrCode className="h-4 w-4 mr-2" />
                    )}
                    Generate QR Code
                  </Button>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Delegate QR Code</DialogTitle>
          </DialogHeader>
          {qrCodeData && (
            <div className="text-center space-y-4">
              <p className="font-medium text-lg">{qrCodeData.delegate_name}</p>
              <img
                src={qrCodeData.qr_code_url}
                alt="QR Code"
                className="mx-auto w-64 h-64 border rounded-lg shadow-sm"
              />
              <p className="text-xs text-slate-500 font-mono bg-slate-100 p-2 rounded">
                {qrCodeData.qr_data}
              </p>
              <Button onClick={downloadQRCode} className="w-full">
                <Download className="h-4 w-4 mr-2" />
                Download QR Code
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DelegateAccessManager;
